import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Checkbox, DatePicker, Form, Input, Modal, Select, message } from "antd";
import dayjs from "dayjs";
import NutritionDrawer, { DrawerFooter } from "@components/nutrition/nutrition-drawer";
import { fetchHealthDoctors } from "@components/health/clients/health-clients-api";
import { docExpFetchDoctorVisitOptions, docExpSendClientToDoctor } from "./doc-exp-api";

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const hm = (value) => (value ? dayjs(value).format("HH:mm") : "");

/**
 * Направление гостя путёвки к врачу (Delphi TFrmJurnExp.AClToVrachExecute +
 * TFrmRegisterSelectTimeVrach — запись на сеанс услуги PRIEM_VRACH).
 */
const DocExpSendToDoctorModal = ({ open, idDoc, client, dateBeg, dateEnd, onClose, onSent }) => {
  const [form] = Form.useForm();
  const [doctors, setDoctors] = useState([]);
  const [sending, setSending] = useState(false);
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [idZal, setIdZal] = useState(0);
  const requestIdRef = useRef(0);

  const dateVizit = Form.useWatch("dateVizit", form);
  const idSotrValue = Form.useWatch("idSotr", form);
  const sessionValue = Form.useWatch("session", form);
  const timeBegValue = Form.useWatch("timeBeg", form);
  const dateKey = dateVizit ? dayjs(dateVizit).format("YYYY-MM-DD") : null;

  const idUsl = pick(options, "idUsl", "IdUsl") || 0;
  const vidPlan = pick(options, "vidPlan", "VidPlan") || 0;
  const timeMax = pick(options, "timeMax", "TimeMax") || 0;
  const uslName = pick(options, "uslName", "UslName") || "";
  const halls = pick(options, "halls", "Halls") || [];
  const sessions = pick(options, "sessions", "Sessions") || [];
  const slots = pick(options, "slots", "Slots") || [];
  const isByTime = idUsl > 0 && vidPlan === 100;

  useEffect(() => {
    if (!open) return;
    fetchHealthDoctors()
      .then((data) => setDoctors(Array.isArray(data) ? data : []))
      .catch(() => setDoctors([]));
    setOptions(null);
    setIdZal(0);
    form.setFieldsValue({
      idSotr: client?.idSotr || undefined,
      dateVizit: dateBeg ? dayjs(dateBeg) : dayjs(),
      isNotSk: false,
      idZal: 0,
      session: undefined,
      timeBeg: undefined
    });
  }, [open, client, dateBeg, form]);

  useEffect(() => {
    form.setFieldsValue({ session: undefined, timeBeg: undefined });
  }, [dateKey, form]);

  useEffect(() => {
    if (!open || !idDoc || !client?.idStr || !dateKey) return;
    const requestId = ++requestIdRef.current;
    setLoadingOptions(true);
    docExpFetchDoctorVisitOptions(idDoc, client.idStr, { date: dateKey, idZal })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setOptions(data || null);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setOptions(null);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoadingOptions(false);
      });
  }, [open, idDoc, client, dateKey, idZal]);

  // VID_PLAN=100: кабинет обязателен — выбираем первый со сеансами.
  useEffect(() => {
    if (!isByTime || halls.length === 0) return;
    const first = pick(halls[0], "idZal", "IdZal");
    const exists = halls.some((h) => pick(h, "idZal", "IdZal") === idZal);
    if (!exists) {
      setIdZal(first);
      form.setFieldsValue({ idZal: first, timeBeg: undefined });
    }
  }, [isByTime, halls, idZal, form]);

  const doctorOptions = useMemo(
    () =>
      doctors.map((d) => ({
        value: d.idSotr ?? d.IdSotr,
        label: d.name ?? d.Name ?? ""
      })),
    [doctors]
  );

  const sessionByKey = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      const key = `${pick(s, "idZal", "IdZal")}-${pick(s, "idStr", "IdStr")}`;
      map[key] = s;
    });
    return map;
  }, [sessions]);

  const sessionOptions = useMemo(
    () =>
      sessions.map((s) => {
        const zal = pick(s, "idZal", "IdZal");
        const str = pick(s, "idStr", "IdStr");
        const cntMax = pick(s, "cntMax", "CntMax") || 0;
        const cntPlan = pick(s, "cntPlan", "CntPlan") || 0;
        const parts = [
          `${hm(pick(s, "timeBeg", "TimeBeg"))}–${hm(pick(s, "timeEnd", "TimeEnd"))}`,
          pick(s, "zalName", "ZalName"),
          pick(s, "sotrName", "SotrName"),
          `(${cntPlan}/${cntMax})`
        ].filter(Boolean);
        return {
          value: `${zal}-${str}`,
          label: parts.join(" · "),
          disabled:
            (cntMax > 0 && cntPlan >= cntMax) || Boolean(pick(s, "isTimeBusy", "IsTimeBusy"))
        };
      }),
    [sessions]
  );

  // Свободные старты внутри интервалов сеансов шагом timeMax (VID_PLAN=100).
  const timeByKey = useMemo(() => {
    const map = {};
    slots.forEach((slot) => {
      if (timeMax <= 0) return;
      const beg = dayjs(pick(slot, "timeBeg", "TimeBeg"));
      const end = dayjs(pick(slot, "timeEnd", "TimeEnd"));
      if (!beg.isValid() || !end.isValid()) return;
      for (let start = beg; start.add(timeMax, "minute").isBefore(end) || start.add(timeMax, "minute").isSame(end); start = start.add(timeMax, "minute")) {
        const key = `${pick(slot, "idZal", "IdZal")}-${pick(slot, "idStr", "IdStr")}-${start.format("HH:mm")}`;
        map[key] = {
          idZal: pick(slot, "idZal", "IdZal"),
          idStrGraf: pick(slot, "idStr", "IdStr"),
          zalName: pick(slot, "zalName", "ZalName") || "",
          timeBeg: start.format("HH:mm"),
          timeEnd: start.add(timeMax, "minute").format("HH:mm"),
          isBlockedUsl: Boolean(pick(slot, "isBlockedUsl", "IsBlockedUsl"))
        };
      }
    });
    return map;
  }, [slots, timeMax]);

  const timeOptions = useMemo(
    () =>
      Object.keys(timeByKey)
        .sort()
        .map((key) => {
          const t = timeByKey[key];
          return {
            value: key,
            label: `${t.timeBeg}–${t.timeEnd} · ${t.zalName}`,
            disabled: t.isBlockedUsl
          };
        }),
    [timeByKey]
  );

  const hallOptions = useMemo(() => {
    const items = halls.map((h) => ({
      value: pick(h, "idZal", "IdZal"),
      label: pick(h, "name", "Name") || ""
    }));
    return isByTime ? items : [{ value: 0, label: "Все кабинеты" }, ...items];
  }, [halls, isByTime]);

  const doSend = async (values) => {
    setSending(true);
    try {
      const body = {
        idSotr: values.idSotr,
        dateVizit: values.dateVizit.format("YYYY-MM-DDTHH:mm:ss"),
        isNotSk: Boolean(values.isNotSk),
        force: true
      };
      if (idUsl > 0 && !isByTime) {
        const session = sessionByKey[values.session];
        body.idZal = pick(session, "idZal", "IdZal");
        body.idStrGraf = pick(session, "idStr", "IdStr");
      }
      if (isByTime) {
        const t = timeByKey[values.timeBeg];
        body.idZal = t.idZal;
        body.idStrGraf = t.idStrGraf;
        body.timeBeg = t.timeBeg;
        body.timeEnd = t.timeEnd;
      }
      const res = await docExpSendClientToDoctor(idDoc, client.idStr, body);
      const resIdZal = pick(res, "idZal", "IdZal") || 0;
      const resDateVizit = pick(res, "dateVizit", "DateVizit");
      if (resIdZal > 0 && resDateVizit) {
        message.success(
          `Клиент отправлен к врачу и записан на ${dayjs(resDateVizit).format("HH:mm DD.MM.YYYY")}`
        );
      } else {
        message.success("Клиент отправлен к врачу");
      }
      const warning = pick(res, "warning", "Warning");
      if (warning) message.warning(warning);
      onSent?.(res);
      onClose?.();
    } catch (e) {
      message.error(e.message || "Не удалось отправить клиента к врачу");
    } finally {
      setSending(false);
    }
  };

  const onOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      if (!e?.errorFields) message.error(e.message || "Не удалось отправить клиента к врачу");
      return;
    }
    // Delphi: подтверждение записи на прошедшее время.
    let visitStart = null;
    if (idUsl > 0 && !isByTime && values.session) {
      visitStart = dayjs(pick(sessionByKey[values.session], "timeBeg", "TimeBeg"));
    } else if (isByTime && values.timeBeg) {
      const t = timeByKey[values.timeBeg];
      visitStart = dayjs(`${dateKey}T${t.timeBeg}:00`);
    }
    if (visitStart?.isValid() && visitStart.isBefore(dayjs())) {
      Modal.confirm({
        title: "Записать на прошедшее время?",
        onOk: () => doSend(values)
      });
      return;
    }
    await doSend(values);
  };

  // Нельзя записать на дату вне периода путёвки (только дата).
  const isOutsideStay = (d) => {
    const beg = dateBeg ? dayjs(dateBeg).startOf("day") : null;
    const end = dateEnd ? dayjs(dateEnd).startOf("day") : null;
    if (!beg?.isValid() || !end?.isValid()) return false;
    const day = d.startOf("day");
    return day.isBefore(beg) || day.isAfter(end);
  };

  const noSessions =
    idUsl > 0 && !loadingOptions && halls.length === 0 && sessions.length === 0;

  const slotChosen = idUsl === 0 || (isByTime ? Boolean(timeBegValue) : Boolean(sessionValue));
  const canSend = Boolean(idSotrValue) && Boolean(dateKey) && slotChosen && !loadingOptions;

  return (
    <NutritionDrawer
      open={open}
      title="Направление к врачу"
      width={440}
      resizable={{ storageKey: "planOnline.docExp.sendToDoctorDrawer.width", min: 360, max: 900 }}
      onClose={onClose}
    >
      <Form form={form} layout="vertical" disabled={sending}>
        <Form.Item
          label="Лечащий врач"
          name="idSotr"
          rules={[{ required: true, message: "Лечащий врач не указан!" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={doctorOptions}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
        {idUsl > 0 && (
          <Form.Item label="Услуга приёма">
            <Input readOnly value={uslName} />
          </Form.Item>
        )}
        <Form.Item
          label="Дата визита"
          name="dateVizit"
          rules={[{ required: true, message: "Укажите дату визита" }]}
        >
          {idUsl > 0 ? (
            <DatePicker
              format="DD.MM.YYYY"
              allowClear={false}
              disabledDate={isOutsideStay}
              style={{ width: "100%" }}
            />
          ) : (
            <DatePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              allowClear={false}
              disabledDate={isOutsideStay}
              style={{ width: "100%" }}
            />
          )}
        </Form.Item>
        {idUsl > 0 && (
          <Form.Item
            label="Кабинет"
            name="idZal"
            rules={isByTime ? [{ required: true, message: "Выберите кабинет" }] : []}
          >
            <Select
              options={hallOptions}
              loading={loadingOptions}
              popupMatchSelectWidth={false}
              onChange={(value) => {
                setIdZal(value || 0);
                form.setFieldsValue({ session: undefined, timeBeg: undefined });
              }}
            />
          </Form.Item>
        )}
        {idUsl > 0 && !isByTime && (
          <Form.Item
            label="Сеанс"
            name="session"
            rules={[{ required: true, message: "Выберите время приёма у доктора" }]}
          >
            <Select
              options={sessionOptions}
              loading={loadingOptions}
              popupMatchSelectWidth={false}
              onChange={(value) => {
                const session = sessionByKey[value];
                const doctor = pick(session, "idSotr", "IdSotr") || 0;
                if (doctor > 0) form.setFieldsValue({ idSotr: doctor });
              }}
            />
          </Form.Item>
        )}
        {isByTime && (
          <Form.Item
            label="Время"
            name="timeBeg"
            rules={[{ required: true, message: "Выберите время приёма у доктора" }]}
          >
            <Select
              options={timeOptions}
              loading={loadingOptions}
              popupMatchSelectWidth={false}
            />
          </Form.Item>
        )}
        {noSessions && (
          <Alert
            type="warning"
            showIcon
            message="Нет сеансов приёма на выбранную дату"
            style={{ marginBottom: 16 }}
          />
        )}
        <Form.Item name="isNotSk" valuePropName="checked">
          <Checkbox>Без санаторно-курортной карты</Checkbox>
        </Form.Item>
      </Form>
      <DrawerFooter
        onSave={onOk}
        onClose={onClose}
        saving={sending}
        saveDisabled={!canSend}
        saveText="Отправить"
        align="left"
      />
    </NutritionDrawer>
  );
};

export default DocExpSendToDoctorModal;
